/**
 * Profile Header Component
 * Displays user's profile picture, name, and basic information
 */

import React from 'react';
import { Edit, Check, X, MapPin, Briefcase, Calendar } from 'lucide-react';

/**
 * Profile Header Component
 * 
 * @param {Object} props - Component props
 * @param {Object} props.profile - User profile data
 * @param {boolean} props.isEditing - Whether the profile is in edit mode
 * @param {Function} props.onEditToggle - Function to toggle edit mode
 * @returns {JSX.Element} Profile header component
 */
const ProfileHeader = ({ profile, isEditing, onEditToggle }) => {
  if (!profile) return null;
  
  // Get profile image or default
  const profileImage = profile.photos && profile.photos.length > 0 
    ? profile.photos[0] 
    : null;
  
  // Get display name or username
  const displayName = profile.basicInfo?.name || profile.basicInfo?.username || 'User';
  
  // Get university name
  const university = profile.academic?.university || '';
  
  // Get study program
  const studyProgram = profile.academic?.degree || '';
  
  // Get year of study
  const yearOfStudy = profile.academic?.yearOfStudy || '';
  
  // Get location
  const location = profile.location?.currentLocation || '';

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Cover Image */}
      <div className="h-32 bg-gradient-to-r from-green-500 to-green-600 relative">
        {profileImage && (
          <div className="absolute inset-0 opacity-20">
            <img 
              src={profileImage} 
              alt="" 
              className="w-full h-full object-cover"
            />
          </div>
        )}
      </div>
      
      <div className="px-4 sm:px-6 relative">
        {/* Profile Image */}
        <div className="absolute -top-16 left-6 border-4 border-white rounded-full overflow-hidden shadow-md">
          <div className="w-32 h-32 bg-gray-200 flex items-center justify-center">
            {profileImage ? (
              <img 
                src={profileImage} 
                alt={displayName} 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-green-100 text-green-500 text-4xl font-bold">
                {displayName.charAt(0)}
              </div>
            )}
          </div>
        </div>
        
        {/* Edit Profile Button - Shown only in edit mode */}
        {isEditing && (
          <div className="absolute top-4 right-4">
            <button
              onClick={onEditToggle}
              className="flex items-center space-x-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-white text-gray-800 hover:bg-gray-100 shadow-sm"
            >
              <X size={16} />
              <span>Cancel</span>
            </button>
          </div>
        )}
        
        {/* Profile Info */}
        <div className="pt-20 pb-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{displayName}</h1>
              <p className="text-gray-500">@{profile.basicInfo?.username}</p>
              
              {/* Bio */}
              {profile.bio?.shortBio && (
                <p className="mt-2 text-gray-600">{profile.bio.shortBio}</p>
              )}
              
              {/* Details */}
              <div className="mt-4 flex flex-wrap gap-y-2 gap-x-4">
                {university && (
                  <div className="flex items-center text-gray-600">
                    <Briefcase size={16} className="mr-1" />
                    <span className="text-sm">{university}</span>
                  </div>
                )}
                
                {studyProgram && (
                  <div className="flex items-center text-gray-600">
                    <Calendar size={16} className="mr-1" />
                    <span className="text-sm">{studyProgram} {yearOfStudy && `(Year ${yearOfStudy})`}</span>
                  </div>
                )}
                
                {location && (
                  <div className="flex items-center text-gray-600">
                    <MapPin size={16} className="mr-1" />
                    <span className="text-sm">{location}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Profile Status */}
            <div className="mt-4 md:mt-0">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Active Now
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;